import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  FieldArray,
  FieldArrayRenderProps,
} from "@progress/kendo-react-form";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MdDelete } from "react-icons/md";
import { FaEdit } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import IconButton from "../../components/common/IconButton";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormTextField from "../../components/formFields/FormTextField";
import {
  setPurchaseOrderIndex,
  setPurchasePreviousOrder,
  setPurchaseSave,
  closePurchaseOrderDeleteConfirmDialog,
} from "./purchaseSlice";
import { hasRequired } from "../../_helper/helper";
import PurchaseItemArray from "./PurchaseItemArray";

const PurchaseOrderDetailArray = (
  fieldArrayRenderProps: FieldArrayRenderProps,
) => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const PurityList = useAppSelector((state) => state.purity.PurityList);
  const PurchaseOrderIndex = useAppSelector(
    (state) => state.purchase.PurchaseOrderIndex,
  );
  const PurchasePreviousOrder = useAppSelector(
    (state) => state.purchase.PurchasePreviousOrder,
  );
  const ItemCategoryList = useAppSelector(
    (state) => state.itemCategory?.ItemCategoryList,
  );
  const ProductSubCategoryList = useAppSelector(
    (state) => state.productsubcategory?.ProductSubCategoryList,
  );
  const ItemList = useAppSelector((state) => state.item.ItemList);
  const fieldName = fieldArrayRenderProps.name || "PurchaseForm";

  const currentIndex =
    PurchaseOrderIndex === 999
      ? fieldArrayRenderProps.value?.length === 0
        ? 0
        : fieldArrayRenderProps.value.length - 1
      : PurchaseOrderIndex;

  const pushElementInArray = React.useCallback(() => {
    const currentPurchaseType =
      fieldArrayRenderProps.formRenderProps.valueGetter("PurchaseType");
    fieldArrayRenderProps?.onPush({
      value: {
        ProductCategoryID: null,
        PurchaseType: currentPurchaseType,
        ProductSubCategoryID: null,
        ProductID: null,
        Purity: null,
        UniqueProductID: null,
        GrossWeight: null,
        NetWeight: null,
        Wastage: null,
        PhinePayble: null,
        TotalRSPayable: null,
        Remarks: "",
        PurchaseFormChild: [],
      },
    });
  }, []);

  const handleDeleteAction = React.useCallback(
    async (index: any) => {
      if (fieldArrayRenderProps?.value[index]) {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closePurchaseOrderDeleteConfirmDialog());
        dispatch(setPurchaseOrderIndex(-1));
        dispatch(closeDialog());
      }
    },
    [fieldArrayRenderProps, dispatch],
  );

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(setPurchaseOrderIndex(index));
    dispatch(openDialog("Purchase-Item-Delete-Dialog"));
  };

  const handleOpenEditDialog = (index: any) => {
    const PurchaseOrderDetails =
      fieldArrayRenderProps.formRenderProps.valueGetter(
        `${fieldName}.${index}`,
      );
    dispatch(
      setPurchasePreviousOrder(
        JSON.parse(JSON.stringify(PurchaseOrderDetails)),
      ),
    );
    dispatch(setPurchaseOrderIndex(index));
    dispatch(setPurchaseSave(true));
    dispatch(openDialog("show-purchase-items-dialog"));
  };

  const handleAddNewDialog = () => {
    dispatch(setPurchaseOrderIndex(999));
    dispatch(setPurchaseSave(true));
    dispatch(setPurchasePreviousOrder(null));
    dispatch(openDialog("show-purchase-items-dialog"));
  };

  const handleFormClose = () => {
    if (PurchaseOrderIndex === 999) {
      if (fieldArrayRenderProps.value?.length > 0) {
        const lastIndex = fieldArrayRenderProps.value.length - 1;
        const lastItem = fieldArrayRenderProps.value[lastIndex];
        if (!lastItem?.ProductCategoryID && !lastItem?.ProductID) {
          fieldArrayRenderProps.onRemove({ index: lastIndex });
        }
      }
    } else {
      fieldArrayRenderProps.formRenderProps.onChange(
        `${fieldName}.${currentIndex}`,
        { value: PurchasePreviousOrder },
      );
    }
    dispatch(closeDialog());
    dispatch(setPurchaseOrderIndex(-1));
    dispatch(setPurchaseSave(false));
    dispatch(setPurchasePreviousOrder(null));
  };

  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();

    await fieldArrayRenderProps.formRenderProps.onSubmit(e);

    const errors = fieldArrayRenderProps.formRenderProps.errors;

    const isParentValid = hasRequired(errors, [
      `${fieldName}.${currentIndex}.ProductCategoryID`,
      `${fieldName}.${currentIndex}.ProductSubCategoryID`,
      `${fieldName}.${currentIndex}.ProductID`,
      `${fieldName}.${currentIndex}.GrossWeight`,
      `${fieldName}.${currentIndex}.NetWeight`,
    ]);

    const childArray =
      fieldArrayRenderProps.value?.[currentIndex]?.PurchaseFormChild || [];

    const childProductIdPaths: string[] = [];

    childArray.forEach((_: any, childIndex: number) => {
      childProductIdPaths.push(
        `${fieldName}.${currentIndex}.PurchaseFormChild.${childIndex}.ItemID`,
      );
    });

    const isChildValid = hasRequired(errors, childProductIdPaths);

    if (isParentValid && isChildValid) {
      dispatch(closeDialog());
      dispatch(setPurchaseOrderIndex(-1));
      dispatch(setPurchaseSave(false));
      dispatch(setPurchasePreviousOrder(null));
    } else {
      console.log("Child ProductID validation failed");
    }
  };

  const filterSubCategory = (ItemCategoryID: number) => {
    if (!ProductSubCategoryList) return [];
    const result = ProductSubCategoryList.filter((item: any) => {
      return ItemCategoryID ? item.item_category_id === ItemCategoryID : true;
    });
    return result.map((item: any) => ({
      value: item?.id,
      label: item?.item_subcategory_name,
    }));
  };

  const filterItems = (
    ItemCategoryID: number,
    ProductSubCategoryID: number,
  ) => {
    if (!ItemList) return [];
    const result = ItemList.filter((item: any) => {
      return (
        (ItemCategoryID ? item.item_category_id === ItemCategoryID : true) &&
        (ProductSubCategoryID
          ? item.item_sub_category_id === ProductSubCategoryID
          : true)
      );
    });
    return result.map((item: any) => ({
      value: item?.id,
      label: item?.item_name,
    }));
  };

  React.useEffect(() => {
    if (
      PurchaseOrderIndex === 999 &&
      dialogName === "show-purchase-items-dialog"
    ) {
      pushElementInArray();
    }
  }, [PurchaseOrderIndex, dialogName, pushElementInArray]);

  // Get display values for table
  const getCategoryName = (ProductCategoryID: number) => {
    return (
      ItemCategoryList?.find((cat: any) => cat.ID === ProductCategoryID)
        ?.category_name || "-"
    );
  };

  const getSubCategoryName = (ProductSubCategoryID: number) => {
    return (
      ProductSubCategoryList?.find(
        (sub: any) => sub.id === ProductSubCategoryID,
      )?.item_subcategory_name || "-"
    );
  };

  const getProductName = (productId: number) => {
    return (
      ItemList?.find((prod: any) => prod.id === productId)?.item_name || "-"
    );
  };

  const filteredValue = fieldArrayRenderProps.value
    ?.map((item: any, index: number) => ({ ...item, originalIndex: index }))
    .filter(
      (item: any) =>
        item?.PurchaseType ===
        fieldArrayRenderProps.formRenderProps.valueGetter("PurchaseType"),
    );

  return (
    <>
      {/* Delete Confirmation Dialog */}
      {dialogName === "Purchase-Item-Delete-Dialog" && (
        <AppDialog>
          <div style={{ width: "30vw" }}>
            <GridLayout gap={{ rows: 10, cols: 10 }}>
              <GridLayoutItem
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography.h4 style={{ margin: 0 }}>
                  Delete Purchase
                </Typography.h4>
                <ImCross
                  style={{
                    color: "white",
                    background: "red",
                    borderRadius: "50%",
                    padding: "5px",
                    fontSize: "24px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    dispatch(closeDialog());
                    dispatch(setPurchaseOrderIndex(-1));
                  }}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this purchase?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                  gap: "10px",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(PurchaseOrderIndex)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </div>
        </AppDialog>
      )}

      {/* Add/Edit Dialog */}
      {dialogName === "show-purchase-items-dialog" && (
        <AppDialog>
          <div style={{ width: "70vw", minHeight: "30vh" }}>
            <GridLayout gap={{ rows: 10, cols: 10 }}>
              <GridLayoutItem
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography.h4 style={{ margin: 0 }}>
                  {PurchaseOrderIndex === 999
                    ? "Add  Purchase Item"
                    : "Edit Purchase Item"}
                </Typography.h4>
                <ImCross
                  style={{
                    color: "white",
                    background: "red",
                    borderRadius: "50%",
                    padding: "5px",
                    fontSize: "24px",
                    cursor: "pointer",
                  }}
                  onClick={handleFormClose}
                />
              </GridLayoutItem>

              <GridLayoutItem
                style={{
                  border: "1px dotted gray",
                  padding: "10px",
                  borderRadius: "8px",
                }}
              >
                <GridLayout
                  style={{
                    marginRight: 30,
                  }}
                  gap={{ rows: 10, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name={`${fieldName}.${currentIndex}.ProductCategoryID`}
                      label="Category"
                      placeholder="Select Category"
                      component={FormSelectionField}
                      options={ItemCategoryList?.map((item) => ({
                        value: item.ID,
                        label: item.category_name,
                      }))}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name={`${fieldName}.${currentIndex}.ProductSubCategoryID`}
                      label="Sub Category"
                      placeholder="Select Sub Category"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={
                        ProductSubCategoryList &&
                        filterSubCategory(
                          fieldArrayRenderProps.formRenderProps.valueGetter(
                            `${fieldName}.${currentIndex}.ProductCategoryID`,
                          ),
                        )
                      }
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name={`${fieldName}.${currentIndex}.ProductID`}
                      label="Product"
                      placeholder="Select Product"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      onChange={(e) =>
                        fieldArrayRenderProps.handleSelectProductInArray({
                          formRenderProps:
                            fieldArrayRenderProps.formRenderProps,
                          index: currentIndex,
                          e,
                        })
                      }
                      options={
                        ItemList &&
                        filterItems(
                          fieldArrayRenderProps.formRenderProps.valueGetter(
                            `${fieldName}.${currentIndex}.ProductCategoryID`,
                          ),
                          fieldArrayRenderProps.formRenderProps.valueGetter(
                            `${fieldName}.${currentIndex}.ProductSubCategoryID`,
                          ),
                        )
                      }
                    />
                  </GridLayoutItem>
                  {fieldArrayRenderProps.formRenderProps.valueGetter(
                    `${fieldName}.${currentIndex}.UniqueProductID`,
                  ) && (
                    <GridLayoutItem>
                      <Field
                        name={`${fieldName}.${currentIndex}.UniqueProductID`}
                        label="Unique Product ID"
                        placeholder="Enter Unique Product ID"
                        disabled={true}
                        component={FormTextField}
                      />
                    </GridLayoutItem>
                  )}

                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${currentIndex}.Purity`}
                      label="Purity"
                      type="number"
                      placeholder="Select Purity"
                      component={FormSelectionField}
                      options={PurityList?.map((purity: any) => {
                        return {
                          value: purity.ID,
                          label: `${purity.Carat}-${purity.purity}`,
                        };
                      })}
                      onChange={(e) => {
                        fieldArrayRenderProps.handleChangePayblePhine({
                          formRenderProps:
                            fieldArrayRenderProps.formRenderProps,
                          index: currentIndex,
                          e,
                        });
                      }}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${currentIndex}.GrossWeight`}
                      label="Gross Weight (gm)"
                      placeholder="0.000"
                      format="n3"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${currentIndex}.NetWeight`}
                      label="Net Weight (gm)"
                      placeholder="0.000"
                      format="n3"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                      onChange={() =>
                        fieldArrayRenderProps.handleChangePayblePhine({
                          formRenderProps:
                            fieldArrayRenderProps.formRenderProps,
                          index: currentIndex,
                        })
                      }
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${currentIndex}.Wastage`}
                      label="Wastage"
                      placeholder="0.00"
                      format="n2"
                      component={FormNumericTextField}
                      onChange={() =>
                        fieldArrayRenderProps.handleChangePayblePhine({
                          formRenderProps:
                            fieldArrayRenderProps.formRenderProps,
                          index: currentIndex,
                        })
                      }
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${currentIndex}.PhinePayble`}
                      label="Phine Payble"
                      placeholder="0.000"
                      format="n3"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                      disabled={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${currentIndex}.TotalRSPayable`}
                      label="Total Payble"
                      component={FormNumericTextField}
                      placeholder="0.000"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name={`${fieldName}.${currentIndex}.Remarks`}
                      label="Remarks"
                      placeholder="Enter Remarks"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                </GridLayout>
              </GridLayoutItem>
              <div
                style={{
                  marginTop: "20px",
                  border: "1px dotted gray",
                  borderRadius: "8px",
                  padding: "5px",
                  overflowX: "auto",
                }}
              >
                <FieldArray
                  formRenderProps={fieldArrayRenderProps.formRenderProps}
                  component={PurchaseItemArray}
                  name={`PurchaseForm.${currentIndex}.PurchaseFormChild`}
                />
              </div>

              <GridLayoutItem
                style={{
                  textAlign: "end",
                  marginTop: "20px",
                  display: "flex",
                  justifyContent: "end",
                  gap: "10px",
                }}
              >
                <Button
                  type="submit"
                  fillMode={"solid"}
                  themeColor={"primary"}
                  onClick={handleSaveItem}
                >
                  {PurchaseOrderIndex === 999 ? "Add Item" : "Update Item"}
                </Button>
                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </div>
        </AppDialog>
      )}

      {/* Header with Add Button */}
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <Typography.h5 style={{ marginBottom: 16 }}>
              Antique Purchase List
            </Typography.h5>
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={handleAddNewDialog}
              type="button"
            >
              Add Item
            </Button>
          </div>
        </GridLayoutItem>
      </GridLayout>

      {/* Table */}
      {filteredValue && filteredValue.length > 0 ? (
        <div
          className="table-responsive"
          style={{
            maxHeight: "50vh",
            scrollbarWidth: "thin",
            overflowX: "auto",
          }}
        >
          <table
            className="table table-striped table-bordered table-hover field-array-table"
            style={{ minWidth: 1800 }}
          >
            <thead>
              <tr>
                <th style={{ width: "10%" }}>Category</th>
                <th style={{ width: "10%" }}>Sub Category</th>
                <th style={{ width: "12%" }}>Product</th>
                <th style={{ width: "8%" }}>Purity</th>
                <th style={{ width: "12%" }}>Unique Product ID</th>
                <th style={{ width: "8%" }}>Gross Weight (gm)</th>
                <th style={{ width: "8%" }}>Net Weight (gm)</th>
                <th style={{ width: "8%" }}>Wastage</th>
                <th style={{ width: "8%" }}>Phine Payble</th>
                <th style={{ width: "8%" }}>Total Payble</th>
                <th style={{ width: "10%" }}>Remarks</th>
                <th style={{ width: "5%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredValue?.map((item: any) => (
                <tr key={item.originalIndex}>
                  <td>{getCategoryName(item?.ProductCategoryID)}</td>
                  <td>{getSubCategoryName(item?.ProductSubCategoryID)}</td>
                  <td>{getProductName(item?.ProductID)}</td>
                  <td>
                    {fieldArrayRenderProps.findPurityName({
                      formRenderProps: fieldArrayRenderProps.formRenderProps,
                      index: item?.originalIndex,
                    })}
                  </td>
                  <td>{item?.UniqueProductID || "-"}</td>
                  <td>{item?.GrossWeight || "-"}</td>
                  <td>{item?.NetWeight || "-"}</td>
                  <td>{item?.Wastage || "-"}</td>
                  <td>{item?.PhinePayble || "-"}</td>
                  <td>{item?.TotalRSPayable || "-"}</td>
                  <td>{item?.Remarks || "-"}</td>
                  <td>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <IconButton
                        type="button"
                        fillMode="flat"
                        size="small"
                        title="Edit"
                        themeColor="success"
                        onClick={() => handleOpenEditDialog(item.originalIndex)}
                      >
                        <FaEdit style={{ fontSize: "20px" }} />
                      </IconButton>
                      <IconButton
                        type="button"
                        fillMode="flat"
                        size="small"
                        title="Delete"
                        themeColor="error"
                        onClick={() =>
                          handleOpenDeleteDialog(item.originalIndex)
                        }
                      >
                        <MdDelete style={{ fontSize: "20px" }} />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
          <Typography.p>
            No items added. Click "Add Item" to add purchase items.
          </Typography.p>
        </div>
      )}
    </>
  );
};

export default PurchaseOrderDetailArray;
